import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Path,
  G,
  Image,
  Font,
} from "@react-pdf/renderer";

// Define styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 8.5,
    lineHeight: 1.2,
    fontFamily: "Helvetica",
    color: "#000",
  },
  bold: {
    fontFamily: "Helvetica-Bold",
    fontWeight: "bold",
  },
  section: {
    marginBottom: 6,
  },
  header: {
    fontSize: 11,
    marginBottom: 15,
    fontFamily: "Helvetica-Bold",
  },
  title: {
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
    marginBottom: 3,
  },
  subTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    textDecoration: "underline",
  },
  row: {
    flexDirection: "row",
    marginBottom: 3,
  },
  label: {
    width: 100,
    fontFamily: "Helvetica-Bold",
  },
  value: {
    flex: 1,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderWidth: 0,
    borderRightWidth: 0,
    borderBottomWidth: 0,
    marginTop: 10,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    paddingVertical: 4,
  },
  tableCol: {
    width: "25%",
  },
  tableCell: {
    margin: "auto",
    marginTop: 3,
    fontSize: 7.5,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginVertical: 6,
  },
  footer: {
    marginTop: 20,
  },
  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 40,
  },
  signatureLine: {
    borderTopWidth: 1,
    borderTopColor: "#000",
    width: "40%",
    marginTop: 20,
  },
  exhibitTitle: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginTop: 30,
    marginBottom: 15,
  },
  exhibitSubTitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 20,
  },
  imagePlaceholder: {
    width: "80%",
    height: 180,
    backgroundColor: "#f0f0f0",
    marginHorizontal: "auto",
    marginBottom: 20,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#ccc",
    borderStyle: "dashed",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
  scanTime: {
    textAlign: "center",
    marginTop: 10,
    fontSize: 10,
  },
  // Reg Form specific
  regFormTitle: {
    textAlign: "center",
    textDecoration: "underline",
    fontFamily: "Helvetica-Bold",
    marginBottom: 10,
  },
  regHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
  },
  hotelInfo: {
    width: "60%",
  },
  contactGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 5,
  },
  contactItem: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 2,
    gap: 4,
  },
  icon: {
    width: 10,
    height: 10,
  },
  infoGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    paddingVertical: 6,
  },
  infoItem: {
    width: "50%",
    flexDirection: "row",
    marginBottom: 2,
  },
  infoLabel: {
    width: 80,
    fontFamily: "Helvetica-Bold",
  },
  infoValue: {
    flex: 1,
  },
  guestDetailsSection: {
    marginTop: 10,
  },
  paymentGrid: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#eee",
    paddingVertical: 5,
  },
  paymentCol: {
    flex: 1,
  },
});

const ChargeBackDocument = ({ data = {} }) => {
  const {
    guestName,
    accountNumber,
    hotelName,
    checkInDate,
    checkOutDate,
    todayDate,
    hotelAddress,
    hotelPhone,
    hotelEmail,
    folioNo,
    crsFolioNo,
    roomType,
    roomNo,
    arrivalDate,
    departureDate,
    status,
    company,
    phone,
    email,
    birthDate,
    idType,
    idNo,
    idValid,
    idCountry,
    vehicleMake,
    vehicleModel,
    licensePlate,
    state,
    specialRequest,
    scanTime,
    idFrontImg,
    idBackImg,
    signatureImg,
    totalCharges,
    totalPayments,
    balanceDue,
    guestAddress,
    adults,
    children,
    infants,
    roomRate,
    tax,
    total,
    serviceCharges,
  } = data;
  console.log("idFrontImg", idFrontImg, idBackImg);
  return (
    <Document>
      {/* Page 1: Legal Response Letter */}
      <Page size="A4" style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.bold}>
            Subject: Formal Legal Response to Chargeback Dispute - {guestName} –
            Registration Card / Folio / Account Number - {accountNumber}
          </Text>
        </View>

        <View style={styles.section}>
          <Text>To whom it may concern,</Text>
        </View>

        <View style={styles.section}>
          <Text>
            I trust this correspondence finds you well. We are in receipt of the
            notice regarding a chargeback concerning a recent stay at{" "}
            <Text style={styles.bold}>{hotelName}</Text>. It is imperative to
            convey our unwavering commitment to engaging in a swift and
            judicious resolution to this matter. To facilitate a rigorous
            investigation, we are hereby submitting the following legally
            substantial documents acquired during the guest's check-in process:
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>
            1. Guest Physically Present Identification Evidence
          </Text>
          <Text> - Exhibit A</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>2. Guest Signed Registration Card</Text>
          <Text> - Exhibit B</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>
            3. Additional Evidence & Notes (If any)
          </Text>
          <Text> - Exhibit C</Text>
        </View>

        <View style={styles.section}>
          <Text>
            For clarity and precision, we underline the specific dates pertinent
            to the guest's check-in and check-out for the disputed reservation:
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.bold}>- Check-In Date: {checkInDate}</Text>
          <Text style={styles.bold}>- Check-Out Date: {checkOutDate}</Text>
        </View>

        <View style={styles.section}>
          <Text>
            These documents represent irrefutable evidence affirming the
            legitimacy of the charges associated with the guest's reservation
            within the stipulated timeframe. We anticipate a rigorous and
            objective assessment of this evidence in pursuit of a just
            resolution.
          </Text>
        </View>

        <View style={styles.section}>
          <Text>
            Should your protocols mandate further elucidation or documentation,
            rest assured we shall promptly comply. Our resolve is unwavering in
            ensuring a transparent and fair resolution to this chargeback
            dispute in accordance with prevailing legal standards.
          </Text>
        </View>

        <View style={styles.section}>
          <Text>
            Your immediate attention to this matter is anticipated, and we rely
            on your expertise to navigate the complexities of this dispute with
            the requisite diligence.
          </Text>
        </View>

        <View style={styles.section}>
          <Text>Yours sincerely,</Text>
        </View>

        <View style={styles.footer}>
          <Text>{hotelName}</Text>
          <Text>{hotelAddress}</Text>
          <Text>
            {hotelPhone}, {hotelEmail}
          </Text>
          <Text>{todayDate}</Text>
        </View>
      </Page>

      {/* Page 3: Exhibits Page */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.exhibitTitle}>Exhibits: A</Text>
        <Text style={styles.exhibitSubTitle}>
          Guest Physically Present Identification Evidence
        </Text>

        <View style={styles.imagePlaceholder}>
          {idFrontImg ? (
            <Image src={idFrontImg} style={styles.image} />
          ) : (
            <Text>ID Front View Placeholder</Text>
          )}
        </View>

        <View style={styles.imagePlaceholder}>
          {idBackImg ? (
            <Image src={idBackImg} style={styles.image} />
          ) : (
            <Text>ID Back View Placeholder</Text>
          )}
        </View>

        <Text style={styles.scanTime}>Scan Time: {scanTime}</Text>
      </Page>

      {/* Page 2: Registration Form */}
      <Page size="A4" style={styles.page}>
        <View style={styles.regHeader}>
          <View style={styles.hotelInfo}>
            <Text style={styles.title}>{hotelName}</Text>
            <View style={styles.contactItem}>
              <Svg
                viewBox="0 0 24 24"
                style={{ width: 10, height: 10, marginRight: 4 }}
              >
                <Path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
                  fill="#000"
                />
              </Svg>
              <Text>{hotelAddress}</Text>
            </View>
            <View style={styles.contactItem}>
              <Svg
                viewBox="0 0 24 24"
                style={{ width: 10, height: 10, marginRight: 4 }}
              >
                <Path
                  d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
                  fill="#000"
                />
              </Svg>
              <Text>{hotelPhone}</Text>
              <Svg
                viewBox="0 0 24 24"
                style={{ width: 10, height: 10, marginLeft: 8, marginRight: 4 }}
              >
                <Path
                  d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
                  fill="#000"
                />
              </Svg>
              <Text>{hotelEmail}</Text>
            </View>
          </View>
        </View>

        <Text style={styles.regFormTitle}>Registration Form</Text>

        <View style={styles.infoGrid}>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Folio #</Text>
            <Text style={styles.infoValue}>: {folioNo}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Date In</Text>
            <Text style={styles.infoValue}>: {arrivalDate}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>CRS Folio #</Text>
            <Text style={styles.infoValue}>: {crsFolioNo}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Date Out</Text>
            <Text style={styles.infoValue}>: {departureDate}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Room Type</Text>
            <Text style={styles.infoValue}>: {roomType}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Status</Text>
            <Text style={styles.infoValue}>: {status}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Room</Text>
            <Text style={styles.infoValue}>: {roomNo}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.infoLabel}>Company</Text>
            <Text style={styles.infoValue}>: {company}</Text>
          </View>
        </View>

        <View style={styles.guestDetailsSection}>
          <Text style={styles.subTitle}>Guest Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Name</Text>
            <Text style={styles.value}>: {guestName}</Text>
            <Text style={styles.label}>Phone</Text>
            <Text style={styles.value}>: {phone}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}>Address</Text>
            <Text style={styles.value}>: {guestAddress}</Text>
            <Text style={styles.label}>Email</Text>
            <Text style={styles.value}>: {email}</Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.label}></Text>
            <Text style={styles.value}></Text>
            <Text style={styles.label}>Birth Date</Text>
            <Text style={styles.value}>: {birthDate}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        {/* Financial Info */}
        <View style={{ flexDirection: "row" }}>
          <View style={{ flex: 1 }}>
            <View style={styles.row}>
              <Text style={styles.label}>Adult(s)</Text>
              <Text style={styles.value}>: {adults}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Child(ren)</Text>
              <Text style={styles.value}>: {children}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Pet(s)</Text>
              <Text style={styles.value}>: {infants}</Text>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <View style={styles.row}>
              <Text style={styles.label}>Rate</Text>
              <Text style={styles.value}>: {roomRate}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Total Rent</Text>
              <Text style={styles.value}>: {total}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Total Tax</Text>
              <Text style={styles.value}>: {tax}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Other Charges</Text>
              <Text style={styles.value}>: {serviceCharges}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.bold}>Total Charges</Text>
              <Text style={styles.value}>: {totalCharges}</Text>
            </View>
          </View>
          <View style={{ flex: 1.5 }}>
            <View style={styles.row}>
              <Text style={styles.label}>Payment Type</Text>
              <Text style={styles.value}>: {}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Credit Card info.</Text>
              <Text style={styles.value}>: {}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Total Payments</Text>
              <Text style={styles.value}>: {totalPayments}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Total Deposit</Text>
              <Text style={styles.value}>: {totalPayments}</Text>
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={{ flexDirection: "row" }}>
          <View style={{ flex: 1 }}>
            <Text style={styles.subTitle}>ID Information</Text>
            <View style={styles.row}>
              <Text style={styles.label}>ID Type</Text>
              <Text style={styles.value}>: {idType}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>ID #</Text>
              <Text style={styles.value}>: {idNo}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>ID Valid</Text>
              <Text style={styles.value}>: {idValid}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>ID Country</Text>
              <Text style={styles.value}>: {idCountry}</Text>
            </View>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={styles.subTitle}>Vehicle Information</Text>
            <View style={styles.row}>
              <Text style={styles.label}>Vehicle Make</Text>
              <Text style={styles.value}>: {vehicleMake}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>Model / Year</Text>
              <Text style={styles.value}>: {vehicleModel}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>License Plate #</Text>
              <Text style={styles.value}>: {licensePlate}</Text>
            </View>
            <View style={styles.row}>
              <Text style={styles.label}>State</Text>
              <Text style={styles.value}>: {state}</Text>
            </View>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.row}>
          <Text style={styles.bold}>Special Request</Text>
          <Text style={styles.value}>: {specialRequest}</Text>
        </View>

        <View style={styles.divider} />

        <View style={styles.section}>
          <Text style={styles.bold}>Notice For Guests:</Text>
          <Text style={{ fontSize: 8 }}>
            Maximum stay is <Text style={styles.bold}>21 consecutive days</Text>{" "}
            per township ordinance. After 21 days, a{" "}
            <Text style={styles.bold}>7-day gap</Text> is required before
            re-check-in. Applies to{" "}
            <Text style={styles.bold}>all reservations</Text>, including
            online/third-party bookings. This property is privately owned and
            the management reserves the right to refuse service to anyone.
            Management will not be responsible for accidents or injury to guests
            or for loss of money, jewelry or valuables of any kind. Management
            will not be responsible for any items left in the room.
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={{ fontSize: 8 }}>
            <Text style={styles.bold}>I AGREE</Text> that my liability for this
            bill is not waived and agree to be held personally liable in the
            event that the indicated person or company failed to pay for any
            part or full amount of these charges including any missing/damaged
            items, etc. If payment is by credit card, you are authorized to
            charge my account for all charges incurred, including any and all
            damages/missing items, etc
          </Text>
        </View>

        <View style={styles.signatureContainer}>
          <View style={{ width: "45%" }}>
            <Text>Guest Signature</Text>
            {signatureImg ? (
              <Image src={signatureImg} style={{ width: 100, height: 40 }} />
            ) : (
              <View style={styles.signatureLine} />
            )}
          </View>
          <View style={{ width: "45%" }}>
            <Text>Desk Clerk Signature</Text>
            <View style={styles.signatureLine} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default ChargeBackDocument;
